// components/layout/RightPanel.jsx
// 右侧面板: 温度控制 / 显示开关 / 选中点详情 / 固相图例 / 数据源分布

import { useSolPred } from '../../hooks/useSolPredStore';
import { PHASE_COLORS } from '../../data/systems';

export default function RightPanel() {
  const {
    selectedSystem,
    temperature, handleTempChange,
    showPrediction, setShowPrediction,
    showRegions, setShowRegions,
    selectedPhase, setSelectedPhase,
    dataPoints, phaseList, predicting,
    selectedPoint, setSelectedPoint,
  } = useSolPred();

  return (
    <aside className="rightpanel">
      {/* 温度 */}
      <div className="panel-section">
        <div className="panel-title">温度控制</div>
        <div className="temp-display">
          <span className="temp-value">{temperature}</span>
          <span className="temp-unit">°C</span>
          <span className={`temp-state ${predicting ? 'predicting' : 'idle'}`}>
            {predicting ? '⟳ infer' : '○ idle'}
          </span>
        </div>
        <div className="temp-slider-wrap">
          <input
            type="range"
            className="temp-slider"
            min={selectedSystem.tempRange[0]}
            max={selectedSystem.tempRange[1]}
            step="1"
            value={temperature}
            onChange={e => handleTempChange(+e.target.value)}
          />
          <div className="temp-range">
            <span>{selectedSystem.tempRange[0]} °C</span>
            <span>↔</span>
            <span>{selectedSystem.tempRange[1]} °C</span>
          </div>
        </div>
      </div>

      {/* 显示开关 */}
      <div className="panel-section">
        <div className="panel-title">显示控制</div>
        <div className="toggle-row">
          <span className="toggle-label">PC-CVAE 预测曲线</span>
          <div className={`toggle ${showPrediction ? 'on' : ''}`} onClick={() => setShowPrediction(v => !v)}>
            <div className="toggle-knob" />
          </div>
        </div>
        <div className="toggle-row">
          <span className="toggle-label">相区底色填充</span>
          <div className={`toggle ${showRegions ? 'on' : ''}`} onClick={() => setShowRegions(v => !v)}>
            <div className="toggle-knob" />
          </div>
        </div>
        {!selectedSystem.hasModel && (
          <div style={{
            marginTop: 10, padding: '8px 10px',
            background: 'rgba(234,88,12,0.08)',
            border: '1px solid rgba(234,88,12,0.2)',
            borderRadius: 6,
            fontSize: 11, color: 'var(--warning)',
          }}>
            ⚠ 该体系模型尚未训练，仅显示实验数据
          </div>
        )}
      </div>

      {/* 选中点详情 */}
      <div className="panel-section">
        <div className="panel-title">选中点详情</div>
        {selectedPoint ? (() => {
          const kind = selectedPoint.kind || 'experimental';
          const kindMeta = {
            experimental: { label: '● 实验数据点', color: '#0284c7', bg: '#e0f2fe' },
            prediction:   { label: '▲ 模型预测点', color: '#7c3aed', bg: '#ede9fe' },
            invariant:    { label: '◆ 共饱和不变点', color: '#0f172a', bg: '#f1f5f9' },
            surface_pred: { label: '■ 预测面采样点', color: '#0d9488', bg: '#ccfbf1' },
          };
          const m = kindMeta[kind] || kindMeta.experimental;
          const phaseColor = selectedPoint.color || (() => {
            const idx = phaseList.indexOf(selectedPoint.solidPhase);
            return PHASE_COLORS[idx % PHASE_COLORS.length] || '#64748b';
          })();

          return (
            <div className="detail-panel">
              <div className="detail-tag" style={{ background: m.bg, color: m.color }}>
                {m.label}
              </div>

              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-key">w({selectedSystem.components[0]})</span>
                  <span className="detail-val">{selectedPoint.w1?.toFixed(4) ?? '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">w({selectedSystem.components[1]})</span>
                  <span className="detail-val">{selectedPoint.w2?.toFixed(4) ?? '—'}</span>
                </div>
                <div className="detail-row highlight">
                  <span className="detail-key">w(H₂O)</span>
                  <span className="detail-val">{selectedPoint.ww?.toFixed(4) ?? '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-key">温度 T</span>
                  <span className="detail-val">{selectedPoint.temp ?? temperature} °C</span>
                </div>
              </div>

              <div className="detail-phase" style={{ borderColor: phaseColor + '40' }}>
                <div style={{
                  fontSize: 9, color: 'var(--text-3)',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontWeight: 700, marginBottom: 4,
                }}>固相 / Solid Phase</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: phaseColor }}>
                  {selectedPoint.solidPhase || '—'}
                </div>
              </div>

              {selectedPoint.source && (
                <div className="detail-row" style={{
                  marginTop: 8, paddingTop: 8,
                  borderTop: '1px solid var(--border)',
                }}>
                  <span className="detail-key">来源</span>
                  <span className="detail-val" style={{
                    fontSize: 10, fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 500,
                  }}>{selectedPoint.source}</span>
                </div>
              )}
              {selectedPoint.uncertainty !== undefined && (
                <div className="detail-row">
                  <span className="detail-key">不确定度 σ</span>
                  <span className="detail-val">±{selectedPoint.uncertainty.toFixed(4)}</span>
                </div>
              )}

              <button className="detail-clear" onClick={() => setSelectedPoint(null)}>
                ✕ 取消选择
              </button>
            </div>
          );
        })() : (
          <div className="detail-empty">
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>◌</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600, marginBottom: 4 }}>
              未选中数据点
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
              点击图中的实验点、预测曲线或不变点，查看详细组成数据
            </div>
          </div>
        )}
      </div>

      {/* 固相图例 */}
      <div className="panel-section">
        <div className="panel-title">固相图例</div>
        <div className="phase-legend">
          <div
            className={`phase-item ${selectedPhase === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedPhase('all')}
          >
            <div className="phase-dot" style={{
              background: 'linear-gradient(45deg, ' + PHASE_COLORS.slice(0, 3).join(',') + ')',
            }} />
            <span className="phase-name">全部固相</span>
            <span className="phase-count">{dataPoints.length}</span>
          </div>
          {phaseList.map((ph, i) => {
            const count = dataPoints.filter(p => p.solidPhase === ph).length;
            return (
              <div
                key={ph}
                className={`phase-item ${selectedPhase === ph ? 'active' : ''}`}
                onClick={() => setSelectedPhase(selectedPhase === ph ? 'all' : ph)}
              >
                <div className="phase-dot" style={{ background: PHASE_COLORS[i % PHASE_COLORS.length] }} />
                <span className="phase-name">{ph}</span>
                <span className="phase-count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 数据源分布 */}
      <div className="panel-section">
        <div className="panel-title">数据来源分布</div>
        {(() => {
          const sources = {};
          dataPoints.forEach(p => { sources[p.source] = (sources[p.source] || 0) + 1; });
          const max = Math.max(...Object.values(sources), 1);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(sources).map(([src, n], i) => (
                <div key={src}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 11 }}>
                    <span style={{ color: 'var(--text-2)' }}>{src}</span>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'var(--text-3)' }}>{n}</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(n / max) * 100}%`,
                      height: '100%',
                      background: PHASE_COLORS[i % PHASE_COLORS.length],
                      borderRadius: 2,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </aside>
  );
}
